import axios, { AxiosResponse } from 'axios';
import { Foldable, map } from 'fp-ts/Array';
import { isRight } from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import { fromFoldable, lookup } from 'fp-ts/Map';
import * as N from 'fp-ts/number';
import { isSome } from 'fp-ts/Option';
import { first } from 'fp-ts/Semigroup';
import * as t from 'io-ts';
import { PathReporter } from 'io-ts/PathReporter';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { apiRoot } from '../../../utils/apiroot';
import { AllCategoriesValidator } from '../exploreApiValidators';

export class InvalidGeoIdError extends Error {
  constructor(e?: string) {
    super(e);
  }
}

export class InvalidCategoryIdError extends Error {
  constructor(e?: string) {
    super(e);
  }
}

const instance = axios.create({
  baseURL: apiRoot(),
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
    Accept: 'application/json',
  },
});

/**
 * rails側に移行したものは即エラーとする
 * @param validator
 * @param res
 */
const strictJsonValidator = <T>(
  validator: t.Type<T>,
  { data }: AxiosResponse<T>,
): T => {
  const result = validator.decode(data);
  if (isRight(result)) {
    return result.right;
  } else {
    const report = PathReporter.report(result);
    throw Error(`${report}\n${data}`);
  }
};

// カテゴリの更新頻度は非常に低いためNode内部でキャッシュしても問題ないと判断した
export type AllCategories = {
  categories: CategoryEntity[];
};

const getAllCategories = async (): Promise<CategoryEntity[]> => {
  const res = await instance.get<AllCategories>(`/explore/all_categories`);
  const { categories } = strictJsonValidator(AllCategoriesValidator, res);
  return categories;
};

let _allCategoriesCache: Promise<CategoryEntity[]> | undefined = undefined;

export const allCategoriesCache = async (): Promise<CategoryEntity[]> => {
  if (!_allCategoriesCache) {
    _allCategoriesCache = getAllCategories();
  }
  return _allCategoriesCache;
};

// 個別カテゴリ取得
const buildCategoryMap = async (): Promise<Map<number, CategoryEntity>> => {
  return pipe(
    await allCategoriesCache(),
    map((c: CategoryEntity) => [c.categoryId, c]),
    fromFoldable(N.Eq, first<CategoryEntity>(), Foldable),
  );
};

let _categoryMap: Promise<Map<number, CategoryEntity>> | undefined = undefined;

export const getCategory = async (
  categoryId: number,
): Promise<CategoryEntity> => {
  if (!_categoryMap) {
    _categoryMap = buildCategoryMap();
  }
  const result = lookup(N.Eq)(categoryId)(await _categoryMap);
  if (isSome(result)) {
    return result.value;
  } else {
    throw new InvalidCategoryIdError(`No such categoryId: ${categoryId}`);
  }
};
